// Counter's home (docs/plans/stations.md, boards CounterSame*): the full app's floor and order rows
// with the chosen order's bill beside them, and no sidebar. These are the same components as the
// Orders page, so the two can't drift apart. An order's menu opens at /tables/:id, in its Counter
// layout.
import { openOrder } from '@ims/core'
import type { OrderDetail } from '@ims/data'
import { Button, Card, EmptyState, cx } from '@ims/ui'
import { Bike, PicnicTable, Plus, ShoppingBag, UtensilsCrossed } from '@ims/ui/icons'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useNow, useSettings } from '../hooks.ts'
import { isOpenOrder } from '../lib/orders.ts'
import { useUser } from '../session.tsx'
import { useCan } from '../workspace.tsx'
import { CounterCheckout } from './counter-checkout.tsx'
import { DeliveriesBoard, NewDeliveryDialog, useActiveDeliveries } from './deliveries.tsx'
import { useFloor, type DiningTable } from './floor.ts'
import { GuestRequests } from './guest-requests.tsx'
import { useBuildContext, useWrite } from './order-hooks.ts'
import {
  FloorPlan,
  GuestsDialog,
  LooseOrders,
  NewTakeawayDialog,
  OrderDialogs,
  type OrderDialog,
} from './restaurant.tsx'
import { OpenList, OrderPeek, serviceTabClass, useOrderActionDeps } from './service.tsx'

type CounterTab = 'tables' | 'takeaway' | 'delivery'

function useTab(): CounterTab {
  const [params] = useSearchParams()
  const tab = params.get('tab')
  return tab === 'takeaway' || tab === 'delivery' ? tab : 'tables'
}

/** The bar's left side on a device set to Counter. */
export function CounterTabs() {
  const floor = useFloor()
  const settings = useSettings()
  const tab = useTab()
  const [starting, setStarting] = useState<'takeaway' | 'delivery' | null>(null)
  const busyTables = new Set(floor?.orders.map((o) => o.order.table_id).filter(Boolean)).size
  const count = (type: 'takeaway' | 'delivery') =>
    floor?.orders.filter((o) => o.order.service_type === type).length ?? 0
  const deliveryOn = !!settings?.delivery_product_id || count('delivery') > 0
  const item = (id: CounterTab, label: string, Icon: typeof Bike, n: number) => (
    <Link
      key={id}
      to={id === 'tables' ? '/station/counter' : `/station/counter?tab=${id}`}
      aria-current={tab === id ? 'page' : undefined}
      className={cx(serviceTabClass(tab === id), 'min-h-10')}
    >
      <Icon className="size-[17px]" aria-hidden />
      {label}
      {n > 0 && <span className="tabular">{n}</span>}
    </Link>
  )
  return (
    <>
      <nav
        aria-label="Tables, takeaway and delivery"
        className="inline-flex gap-1 rounded-xl border border-border bg-surface p-1"
      >
        {item('tables', 'Tables', PicnicTable, busyTables)}
        {item('takeaway', 'Takeaway', ShoppingBag, count('takeaway'))}
        {deliveryOn && item('delivery', 'Delivery', Bike, count('delivery'))}
      </nav>
      <Button
        variant="secondary"
        className="ml-1.5 h-11"
        icon={<Plus className="size-4" />}
        onClick={() => setStarting(tab === 'delivery' ? 'delivery' : 'takeaway')}
      >
        {tab === 'delivery' ? 'New delivery' : 'New takeaway'}
      </Button>
      {starting === 'takeaway' && <NewTakeawayDialog onClose={() => setStarting(null)} />}
      {starting === 'delivery' && <NewDeliveryDialog onClose={() => setStarting(null)} />}
    </>
  )
}

/** The side panel shows from 1024px; below that a table opens the order itself. */
const wide = () => window.matchMedia('(min-width: 64rem)').matches

export function StationCounterScreen() {
  const floor = useFloor()
  const tab = useTab()
  const settings = useSettings()
  const now = useNow()
  const navigate = useNavigate()
  const me = useUser().id
  const canServe = useCan('waiter')
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const actions = useOrderActionDeps()
  const deliveries = useActiveDeliveries()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [opening, setOpening] = useState<DiningTable | null>(null)
  // Held here, not on the pane: the pane goes when the sale closes the order, while the receipt is
  // still being printed.
  const [checkout, setCheckout] = useState<{ detail: OrderDetail; where: string } | null>(null)
  const [dialog, setDialog] = useState<OrderDialog | null>(null)
  if (!floor) return null
  const open = floor.orders.filter((o) => isOpenOrder(o.order))
  const ordersOf = (id: string) => open.filter((o) => o.order.table_id === id)
  const chosen = open.find((o) => o.order.id === selectedId) ?? null
  const onCheckout = (detail: OrderDetail, where: string) => setCheckout({ detail, where })
  const paying =
    checkout && (open.find((o) => o.order.id === checkout.detail.order.id) ?? checkout.detail)
  const start = async (table: DiningTable, covers: number) => {
    const built = openOrder(context(), { tableId: table.id, covers, serverId: me })
    if (await write(() => built.ops)) navigate(`/tables/${built.order.id}`)
  }
  const tap = (table: DiningTable) => {
    const orders = ordersOf(table.id)
    if (orders.length === 0) {
      if (canServe) setOpening(table)
    } else if (!wide()) {
      navigate(`/tables/${orders[0]!.order.id}`)
    } else {
      // A table with two bills: each tap goes on to the next.
      const next = orders.findIndex((o) => o.order.id === selectedId) + 1
      setSelectedId(orders[next % orders.length]!.order.id)
    }
  }
  const tables = floor.tables.filter((t) => t.is_active)
  return (
    <div className="p-3.5">
      {tab === 'tables' ? (
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_372px] lg:items-start lg:gap-3.5">
          <div className="min-w-0">
            <GuestRequests requests={floor.requests} settings={settings} now={now} />
            {tables.length === 0 ? (
              <Card className="p-0">
                <EmptyState
                  icon={<UtensilsCrossed className="size-6" />}
                  title="No tables yet"
                  description="A manager adds tables from the full app, in Settings."
                />
              </Card>
            ) : (
              <FloorPlan floor={floor} onTap={tap} selectedTableId={chosen?.order.table_id ?? ''} />
            )}
            <LooseOrders floor={floor} />
          </div>
          {chosen ? (
            <OrderPeek
              key={chosen.order.id}
              detail={chosen}
              floor={floor}
              actions={actions}
              canServe={canServe}
              onCheckout={onCheckout}
            />
          ) : (
            <Card className="p-0 max-lg:hidden">
              <EmptyState
                icon={<UtensilsCrossed className="size-6" />}
                title="Choose a table"
                description="Its bill opens here. Tap a free table to start an order."
              />
            </Card>
          )}
        </div>
      ) : (
        <>
          <OpenList
            floor={floor}
            type={tab}
            search=""
            deliveryOn={!!settings?.delivery_product_id}
            onCheckout={onCheckout}
          />
          {tab === 'delivery' && <DeliveriesBoard details={deliveries} canServe={canServe} />}
        </>
      )}
      {opening && (
        <GuestsDialog
          title={`Open ${opening.name}`}
          initial={Math.min(opening.seats ?? 2, 2)}
          confirmLabel="Start order"
          busy={busy}
          onClose={() => setOpening(null)}
          onConfirm={(covers) => void start(opening, covers)}
        />
      )}
      {paying && checkout && (
        <CounterCheckout
          detail={paying}
          where={checkout.where}
          onClose={() => setCheckout(null)}
          onSplit={() => setDialog('split')}
        />
      )}
      {paying && dialog && (
        <OrderDialogs
          detail={paying}
          floor={floor}
          dialog={dialog}
          onClose={() => {
            setDialog(null)
            setCheckout(null)
          }}
          inList
        />
      )}
    </div>
  )
}
